import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  IconButton,
  Notice,
  Segmented,
  StageTracker,
  TextField,
  iconBack,
  useSnackbar,
} from '@sauda/ui/kit';
import { BrandLogo } from '../app/BrandLogo.js';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, api } from '../lib/api.js';

const MOBILE = /^(?:\+?91)?[6-9]\d{9}$/;
const clean = (value: string): string => value.replace(/[\s-]/g, '');

const DRAFT_KEY = 'neuu.onboarding';

interface Draft {
  step: number;
  ownerName: string;
  name: string;
  city: string;
  phone: string;
  sameWhatsapp: boolean;
  whatsapp: string;
  team: boolean;
  branchName: string;
  branchArea: string;
}

/** What was typed before a refresh. Kept in this tab only, and never holds a password or a code. */
function readDraft(): Partial<Draft> {
  try {
    const raw = window.sessionStorage.getItem(DRAFT_KEY);
    return raw === null ? {} : (JSON.parse(raw) as Partial<Draft>);
  } catch {
    return {};
  }
}

function writeDraft(draft: Draft | null): void {
  try {
    if (draft === null) window.sessionStorage.removeItem(DRAFT_KEY);
    else window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Storage can be blocked; the form still works, it just forgets on refresh.
  }
}

/** Three short steps: who you are, your agency, your first branch. The trial starts when it is created. */
export function OnboardingScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const snack = useSnackbar();
  const [saved] = useState(readDraft);
  const [step, setStep] = useState(saved.step ?? 0);
  const [ownerName, setOwnerName] = useState(saved.ownerName ?? '');
  const [name, setName] = useState(saved.name ?? '');
  const [city, setCity] = useState(saved.city ?? 'Pune');
  const [phone, setPhone] = useState(saved.phone ?? '');
  const [sameWhatsapp, setSameWhatsapp] = useState(saved.sameWhatsapp ?? true);
  const [whatsapp, setWhatsapp] = useState(saved.whatsapp ?? '');
  const [team, setTeam] = useState(saved.team ?? false);
  const [branchName, setBranchName] = useState(saved.branchName ?? '');
  const [branchArea, setBranchArea] = useState(saved.branchArea ?? '');
  useEffect(() => {
    writeDraft({
      step,
      ownerName,
      name,
      city,
      phone,
      sameWhatsapp,
      whatsapp,
      team,
      branchName,
      branchArea,
    });
  }, [step, ownerName, name, city, phone, sameWhatsapp, whatsapp, team, branchName, branchArea]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ accessToken: string }>('/onboarding/agency', {
        method: 'POST',
        body: {
          name: name.trim(),
          city: city.trim() === '' ? 'Pune' : city.trim(),
          branchName: branchName.trim(),
          ...(branchArea.trim() === '' ? {} : { branchArea: branchArea.trim() }),
          ownerName: ownerName.trim(),
          phone: clean(phone),
          whatsapp: clean(sameWhatsapp ? phone : whatsapp),
        },
      });
      writeDraft(null);
      await auth.adopt(created.accessToken);
      snack({ text: t('onboarding.trialStarted'), duration: 6000 });
      void navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError(
          e.code === 'TRIAL_ALREADY_USED'
            ? t('onboarding.trialUsed')
            : t('onboarding.alreadyCreated'),
        );
      } else setError(t('onboarding.generic'));
    } finally {
      setBusy(false);
    }
  };

  const stages = [
    { key: 'you', label: t('onboarding.stepYou') },
    { key: 'agency', label: t('onboarding.stepAgency') },
    { key: 'branch', label: t(team ? 'onboarding.stepBranch' : 'onboarding.stepWorkFrom') },
  ];
  const valid =
    step === 0
      ? ownerName.trim().length >= 2
      : step === 1
        ? name.trim().length >= 2 &&
          MOBILE.test(clean(phone)) &&
          (sameWhatsapp || MOBILE.test(clean(whatsapp)))
        : branchName.trim().length > 0;
  return (
    <div className="sign-page">
      {step > 0 ? (
        <IconButton icon={iconBack} label={t('app.back')} onClick={() => setStep(step - 1)} />
      ) : null}
      <BrandLogo />
      <StageTracker label={t('onboarding.progress')} stages={stages} current={step} />
      <h1 className="k-t-headline">{t('onboarding.title')}</h1>
      <form
        className="sign-inner"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          if (step < 2) setStep(step + 1);
          else void create();
        }}
      >
        {step === 0 ? (
          <>
            <p className="k-t-body-lg k-muted">{t('onboarding.aboutYou')}</p>
            <TextField
              label={t('onboarding.yourName')}
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              autoComplete="name"
              supporting={t('onboarding.yourNameWhy')}
              required
            />
          </>
        ) : step === 1 ? (
          <>
            <p className="k-t-body-lg k-muted">{t('onboarding.aboutAgency')}</p>
            <TextField
              label={t('onboarding.agencyName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <TextField
              label={t('onboarding.city')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <TextField
              label={t('onboarding.agencyPhone')}
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              prefix="+91"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              supporting={t('onboarding.agencyPhoneWhy')}
              required
            />
            <span className="k-t-label-lg">{t('onboarding.size')}</span>
            <Segmented
              label={t('onboarding.size')}
              value={team ? 'team' : 'solo'}
              onChange={(v) => setTeam(v === 'team')}
              options={[
                { value: 'solo', label: t('onboarding.sizeSolo') },
                { value: 'team', label: t('onboarding.sizeTeam') },
              ]}
            />
            <Checkbox
              label={t('onboarding.sameWhatsapp')}
              checked={sameWhatsapp}
              onChange={(e) => setSameWhatsapp(e.target.checked)}
            />
            {sameWhatsapp ? null : (
              <TextField
                label={t('onboarding.agencyWhatsapp')}
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                prefix="+91"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                required
              />
            )}
          </>
        ) : (
          <>
            <p className="k-t-body-lg k-muted">{t('onboarding.aboutBranch')}</p>
            <TextField
              label={t(team ? 'onboarding.branchName' : 'onboarding.workFrom')}
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              supporting={team ? undefined : t('onboarding.workFromWhy')}
              required
            />
            {team ? (
              <TextField
                label={t('onboarding.branchArea')}
                value={branchArea}
                onChange={(e) => setBranchArea(e.target.value)}
                supporting={t('onboarding.moreBranches')}
              />
            ) : null}
          </>
        )}
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        <div className="sign-page__spacer" />
        <Button type="submit" size="lg" block loading={busy} disabled={!valid}>
          {step < 2 ? t('onboarding.next') : t('onboarding.create')}
        </Button>
      </form>
    </div>
  );
}
